import styles from './BudgetTrackerCard.module.scss';
import React from 'react';
import BudgetProgressBar from 'src/components/atomic/molecules/budgetTrackerCard/helpers/BudgetProgressBar';
import TrackerIconButtons from 'src/components/atomic/molecules/budgetTrackerCard/helpers/TrackerIconButtons';
import SpentRemainingRow from 'src/components/atomic/molecules/budgetTrackerCard/helpers/SpentRemainingRow';
import BudgetService, { BUDGET_OVERALL_ID } from 'src/services/budget.service';
import { useSyncingAccounts } from 'src/hooks/useSyncingAccounts';
import { BudgetSummaryStatus } from 'src/types/budget-summary-status.enum';
import Selectors from 'src/utils/selectors';
import TrackerHeader from 'src/components/atomic/molecules/budgetTrackerCard/helpers/TrackerHeader';
import adobeTags from 'src/types/adobe-tags';
import { BudgetModel } from 'src/types/budget.model';

interface BudgetTrackerCardProps {
    budget: BudgetModel;
    parentName?: string;
}

const getTrackerStatus = (actualAmount: number, budgetAmount: number): BudgetSummaryStatus => {
    const progress: number = BudgetService.getProgressPercent(actualAmount, budgetAmount);
    let status = BudgetSummaryStatus.ON_TRACK;
    if (progress > 75) {
        status = BudgetSummaryStatus.AT_RISK;
    }
    if (budgetAmount - actualAmount < 0) {
        status = BudgetSummaryStatus.OVER;
    }
    return status;
};

export default function BudgetTrackerCard({ budget, parentName }: BudgetTrackerCardProps): JSX.Element {
    const { isSyncingAccounts } = useSyncingAccounts();
    const remaining: number = budget.monthlyAmount - budget.currentlySpent;
    const progressPercent: number = Math.min(
        BudgetService.getProgressPercent(budget.currentlySpent, budget.monthlyAmount),
        100
    );
    const status: BudgetSummaryStatus = getTrackerStatus(budget.currentlySpent, budget.monthlyAmount);
    const tagName: string =
        budget.groupId === BUDGET_OVERALL_ID
            ? adobeTags.CurrentMonth.TotalMonthlySpending
            : adobeTags.CurrentMonth.EachCategory;

    return (
        <div
            className={`${styles.budgetTrackerCard} ${parentName ? styles.subcategory : ''}`}
            data-cy={Selectors.SpendSmart.BUDGET_TRACKER_CARD}
        >
            {parentName && (
                <div
                    className={isSyncingAccounts ? styles.skeletonBar : styles.blueBar}
                    data-cy={Selectors.SpendSmart.BUDGET_TRACKER_CARD_BAR}
                />
            )}
            <div className={styles.trackerCardContent}>
                <div className={styles.topRow}>
                    <TrackerHeader
                        budgetGroupName={budget.groupName}
                        budgetMonthlyAmount={budget.monthlyAmount}
                        parentName={parentName}
                        status={status}
                    />
                    <TrackerIconButtons
                        budgetGroupName={budget.groupName}
                        budgetGroupId={budget.groupId}
                        className={styles.top}
                        tagName={tagName}
                    />
                </div>
                <SpentRemainingRow
                    spentAmount={budget.currentlySpent}
                    remainingAmount={remaining}
                    budgetGroupName={budget.groupName}
                    budgetGroupId={budget.groupId}
                    status={status}
                    tagName={tagName}
                />
                <BudgetProgressBar
                    spentAmount={budget.currentlySpent}
                    progressPercent={progressPercent}
                    overAmount={remaining * -1}
                    targetAmount={budget.monthlyAmount}
                    status={status}
                />
                <TrackerIconButtons
                    budgetGroupName={budget.groupName}
                    budgetGroupId={budget.groupId}
                    className={styles.bottom}
                    tagName={tagName}
                />
            </div>
        </div>
    );
}
